interface CTASectionProps {
  onOpenModal: () => void;
}

export default function CTASection({ onOpenModal }: CTASectionProps) {
  return (
    <section id="cta" className="w-full py-10 md:py-20 relative overflow-hidden">
      <style>{`
        @keyframes kenBurns {
          0% { transform: scale(1) translate(0, 0); }
          50% { transform: scale(1.08) translate(-1%, -1%); }
          100% { transform: scale(1) translate(0, 0); }
        }
        @keyframes floatUp {
          0% { transform: translateY(0) translateX(0); opacity: 0; }
          10% { opacity: 0.6; }
          90% { opacity: 0.6; }
          100% { transform: translateY(-120px) translateX(20px); opacity: 0; }
        }
        @keyframes floatSlow {
          0% { transform: translateY(0) translateX(0); opacity: 0; }
          15% { opacity: 0.5; }
          85% { opacity: 0.5; }
          100% { transform: translateY(-80px) translateX(-30px); opacity: 0; }
        }
        @keyframes pulseGlow {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.6; }
        }
        .animate-ken-burns {
          animation: kenBurns 20s ease-in-out infinite;
        }
        .animate-float-up {
          animation: floatUp 8s ease-in-out infinite;
        }
        .animate-float-slow {
          animation: floatSlow 12s ease-in-out infinite;
        }
        .animate-pulse-glow {
          animation: pulseGlow 4s ease-in-out infinite;
        }
      `}</style>
      
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 animate-ken-burns">
          <img
            alt="Gracie and Lily in a magical garden"
            className="w-full h-full object-cover"
            src="https://static.readdy.ai/image/ff63a95a3d068db59710e28833339873/6bf15ec756f3863da886418f17f91889.png"
          />
        </div>

        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-primary-950/95"></div>
        
        {/* Floating particles */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Large soft orbs */}
          <div className="absolute top-[20%] left-[10%] w-3 h-3 rounded-full bg-white/40 animate-float-up animate-pulse-glow" style={{ animationDelay: '0s' }}></div>
          <div className="absolute top-[60%] left-[80%] w-2 h-2 rounded-full bg-white/30 animate-float-slow" style={{ animationDelay: '2s' }}></div>
          <div className="absolute top-[40%] left-[30%] w-4 h-4 rounded-full bg-white/20 animate-float-up" style={{ animationDelay: '4s' }}></div>
          <div className="absolute top-[70%] left-[15%] w-2 h-2 rounded-full bg-white/35 animate-float-slow animate-pulse-glow" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-[15%] left-[60%] w-3 h-3 rounded-full bg-white/25 animate-float-up" style={{ animationDelay: '6s' }}></div>
          <div className="absolute top-[50%] left-[70%] w-2 h-2 rounded-full bg-white/30 animate-float-slow" style={{ animationDelay: '3s' }}></div>
          <div className="absolute top-[80%] left-[50%] w-3 h-3 rounded-full bg-white/20 animate-float-up" style={{ animationDelay: '5s' }}></div>
          <div className="absolute top-[30%] left-[90%] w-2 h-2 rounded-full bg-white/35 animate-float-slow animate-pulse-glow" style={{ animationDelay: '7s' }}></div>
          
          {/* Sparkle stars */}
          <div className="absolute top-[25%] left-[20%] text-white/50 text-sm animate-float-up" style={{ animationDelay: '1.5s' }}>✨</div>
          <div className="absolute top-[55%] left-[75%] text-white/40 text-xs animate-float-slow" style={{ animationDelay: '3.5s' }}>✨</div>
          <div className="absolute top-[45%] left-[45%] text-white/30 text-sm animate-float-up" style={{ animationDelay: '5.5s' }}>✨</div>
          <div className="absolute top-[75%] left-[25%] text-white/45 text-xs animate-float-slow" style={{ animationDelay: '0.5s' }}>✨</div>
          <div className="absolute top-[35%] left-[85%] text-white/35 text-sm animate-float-up" style={{ animationDelay: '7.5s' }}>✨</div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 md:px-8 flex flex-col items-center gap-4 md:gap-6 text-center relative z-10 py-8 md:py-12">
        <div className="bg-accent-500 text-white px-5 py-2 rounded-full font-bold text-sm inline-flex items-center gap-2">
          🎁 LIMITED TIME OFFER
        </div>

        <h2 className="text-3xl md:text-5xl font-black text-white leading-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
          Start Your Magical Adventure Today!
        </h2>

        <p className="text-white text-base md:text-lg max-w-2xl leading-relaxed drop-shadow-[0_2px_6px_rgba(0,0,0,0.85)]">
          Join thousands of families who have discovered the joy of Gracie and Lily's adventures. Perfect for bedtime stories, learning moments, and creating lasting memories!
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 w-full mt-2 md:mt-4">
          <div className="bg-white rounded-2xl py-5 md:py-6 px-3 flex flex-col items-center gap-2 shadow-lg">
            <span className="text-2xl md:text-3xl font-black text-primary-500">13</span>
            <span className="text-foreground-700 text-xs md:text-sm font-semibold">Adventures</span>
          </div>
          <div className="bg-white rounded-2xl py-5 md:py-6 px-3 flex flex-col items-center gap-2 shadow-lg">
            <span className="text-2xl md:text-3xl font-black text-primary-500">3-8</span>
            <span className="text-foreground-700 text-xs md:text-sm font-semibold">Age Range</span>
          </div>
          <div className="bg-white rounded-2xl py-5 md:py-6 px-3 flex flex-col items-center gap-2 shadow-lg">
            <span className="text-lg md:text-xl font-black text-primary-500 leading-tight">⭐⭐⭐⭐⭐</span>
            <span className="text-foreground-700 text-xs md:text-sm font-semibold">Rated Stories</span>
          </div>
          <div className="bg-white rounded-2xl py-5 md:py-6 px-3 flex flex-col items-center gap-2 shadow-lg">
            <span className="text-2xl md:text-3xl font-black text-primary-500">2</span>
            <span className="text-foreground-700 text-xs md:text-sm font-semibold">Real Dogs!</span>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 md:gap-4 mt-2 flex-wrap justify-center">
          <a
            href="https://gladventures.gumroad.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white text-primary-500 px-6 md:px-8 py-3 md:py-3.5 rounded-lg font-bold text-base whitespace-nowrap cursor-pointer hover:bg-background-100 transition-colors text-center shadow-lg"
          >
            Shop All Books
          </a>
          <button 
            onClick={() => onOpenModal()}
            className="border-2 border-white text-white px-6 md:px-8 py-3 md:py-3.5 rounded-lg font-bold text-base whitespace-nowrap cursor-pointer hover:bg-white hover:text-primary-500 transition-colors"
          >
            Claim Free Gift 🎁
          </button>
        </div>

        <p className="text-white text-xs md:text-sm mt-1 drop-shadow-[0_1px_4px_rgba(0,0,0,0.85)]">Instant digital access • Fast shipping on print books • 100% satisfaction guaranteed</p>
      </div>
    </section>
  );
}